import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Skeleton,
  Link,
  Paper,
  alpha,
  Button,
} from "@mui/material";
import ReportProblemRounded from "@mui/icons-material/ReportProblemRounded";
import DescriptionRounded from "@mui/icons-material/DescriptionRounded";
import AccessTimeRounded from "@mui/icons-material/AccessTimeRounded";
import AttachMoneyRounded from "@mui/icons-material/AttachMoneyRounded";
import WarningRounded from "@mui/icons-material/WarningRounded";
import MoneyOffRounded from "@mui/icons-material/MoneyOffRounded";
import GroupsRounded from "@mui/icons-material/GroupsRounded";
import SavingsRounded from "@mui/icons-material/SavingsRounded";
import { useQuery } from "@tanstack/react-query";
import { dashboardApi } from "../../services/api";
import type { ProjectDetailsFilterRequest } from "../../types/api";
import type { ProjectsFilterRequest } from "../../types/projects";
import { formatCurrency } from "../../utils/FormatUtils";
import { SEARCE_COLORS } from "../../theme";

export const PROJECT_DETAILS_SCROLL_ID = "project-details-section";

export type ActionableInsightCardId =
  | "rag-red-accounts"
  | "rag-red-projects"
  | "over-budget"
  | "timeline-breach"
  | "pending-recognition"
  | "pending-realization"
  | "no-revenue-ip"
  | "unallocated-capacity";

/** Active-ish delivery statuses for RAG / cost / schedule insight cards (matches Project Details drill-in). */
const INSIGHT_ACTIVE_PROJECT_STATUSES = ["In Progress", "On Hold"] as const;

/** Local calendar date YYYY-MM-DD offset from today (00:00). */
function isoDateLocalDaysOffset(daysFromToday: number): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + daysFromToday);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Client-side Project Details filters aligned with each insight card (merged after page-level filters).
 */
export function getInsightTableFilterPatch(
  cardId: ActionableInsightCardId
): Partial<ProjectDetailsFilterRequest> {
  const clearEfforts: string[] = [];
  switch (cardId) {
    case "rag-red-accounts":
      return {
        statuses: [...INSIGHT_ACTIVE_PROJECT_STATUSES],
        rag_statuses: ["Red"],
        efforts_deviation: clearEfforts,
        timeline_breach: undefined,
        unrecognized_revenue_min: undefined,
        unrecognized_revenue_max: undefined,
        sort_by: "account",
        sort_order: "asc",
      };
    case "rag-red-projects":
      return {
        statuses: [...INSIGHT_ACTIVE_PROJECT_STATUSES],
        rag_statuses: ["Red"],
        efforts_deviation: clearEfforts,
        timeline_breach: undefined,
        unrecognized_revenue_min: undefined,
        unrecognized_revenue_max: undefined,
        sort_by: "start_date",
        sort_order: "desc",
      };
    case "over-budget":
      return {
        statuses: [...INSIGHT_ACTIVE_PROJECT_STATUSES],
        efforts_deviation: ["Over"],
        rag_statuses: [],
        timeline_breach: undefined,
        unrecognized_revenue_min: undefined,
        unrecognized_revenue_max: undefined,
        sort_by: "start_date",
        sort_order: "desc",
      };
    case "timeline-breach":
      return {
        statuses: [...INSIGHT_ACTIVE_PROJECT_STATUSES],
        timeline_breach: "Yes",
        efforts_deviation: clearEfforts,
        unrecognized_revenue_min: undefined,
        unrecognized_revenue_max: undefined,
        sort_by: "start_date",
        sort_order: "desc",
      };
    case "pending-recognition":
      return {
        /** Empty = no status restriction (all statuses: In Progress, On Hold, Closed, etc.). */
        statuses: [],
        unrecognized_revenue_min: 0.01,
        unrecognized_revenue_max: undefined,
        efforts_deviation: clearEfforts,
        timeline_breach: undefined,
        sort_by: "start_date",
        sort_order: "desc",
      };
    case "pending-realization":
      return {
        statuses: [],
        unrealized_revenue_min: 0.01,
        efforts_deviation: clearEfforts,
        timeline_breach: undefined,
        unrecognized_revenue_min: undefined,
        unrecognized_revenue_max: undefined,
        sort_by: "start_date",
        sort_order: "desc",
      };
    case "no-revenue-ip":
      return {
        statuses: ["In Progress"],
        recognized_revenue_min: 0,
        recognized_revenue_max: 0,
        start_date_to: isoDateLocalDaysOffset(-30),
        rag_statuses: [],
        efforts_deviation: clearEfforts,
        timeline_breach: undefined,
        unrecognized_revenue_min: undefined,
        unrecognized_revenue_max: undefined,
        sort_by: "start_date",
        sort_order: "asc",
      };
    case "unallocated-capacity":
      return {
        /** All project statuses — do not restrict by status. */
        statuses: [],
        unassigned_role_count_min: 1,
        rag_statuses: [],
        efforts_deviation: clearEfforts,
        timeline_breach: undefined,
        unrecognized_revenue_min: undefined,
        unrecognized_revenue_max: undefined,
        sort_by: "unassigned_role_count",
        sort_order: "desc",
      };
    default:
      return {};
  }
}

type InsightTone = "priority" | "cost" | "schedule" | "revenue" | "gap" | "capacity";

interface InsightCardDef {
  id: ActionableInsightCardId;
  badge: string;
  tone: InsightTone;
  title: string;
  metric: string;
  subtext: string;
  actionLabel: string;
  icon: React.ReactNode;
}

const toneColors: Record<
  InsightTone,
  { border: string; iconBg: string; iconFg: string; badgeBg: string }
> = {
  priority: {
    border: SEARCE_COLORS.error,
    iconBg: alpha(SEARCE_COLORS.error, 0.12),
    iconFg: SEARCE_COLORS.error,
    badgeBg: alpha(SEARCE_COLORS.error, 0.12),
  },
  cost: {
    border: SEARCE_COLORS.warning,
    iconBg: alpha(SEARCE_COLORS.warning, 0.18),
    iconFg: SEARCE_COLORS.warningDark,
    badgeBg: alpha(SEARCE_COLORS.warning, 0.2),
  },
  schedule: {
    border: SEARCE_COLORS.warning,
    iconBg: alpha(SEARCE_COLORS.warning, 0.18),
    iconFg: SEARCE_COLORS.warningDark,
    badgeBg: alpha(SEARCE_COLORS.warning, 0.2),
  },
  revenue: {
    border: SEARCE_COLORS.primary,
    iconBg: alpha(SEARCE_COLORS.primary, 0.1),
    iconFg: SEARCE_COLORS.primary,
    badgeBg: alpha(SEARCE_COLORS.primary, 0.1),
  },
  gap: {
    border: SEARCE_COLORS.secondary,
    iconBg: alpha(SEARCE_COLORS.secondary, 0.12),
    iconFg: SEARCE_COLORS.secondary_600,
    badgeBg: alpha(SEARCE_COLORS.secondary, 0.12),
  },
  capacity: {
    border: SEARCE_COLORS.info,
    iconBg: alpha(SEARCE_COLORS.info, 0.12),
    iconFg: SEARCE_COLORS.info_700,
    badgeBg: alpha(SEARCE_COLORS.info, 0.14),
  },
};

function buildMinimalProjectDetailsFilters(
  projectFilters?: ProjectsFilterRequest
): ProjectDetailsFilterRequest {
  return {
    ...(projectFilters && {
      opportunity_closed_years: projectFilters.opportunity_closed_years,
      opportunity_closed_quarters: projectFilters.opportunity_closed_quarters,
      opportunity_stage: projectFilters.opportunity_stage,
      project_account_bus: projectFilters.project_account_bus,
      project_owners: projectFilters.project_owners,
      delivery_bus: projectFilters.delivery_bus,
      delivery_sub_bus: projectFilters.delivery_sub_bus,
      accounts: projectFilters.accounts,
      statuses: projectFilters.statuses,
      rag_statuses: projectFilters.rag_statuses,
      efforts_deviation: projectFilters.efforts_deviation,
      smm_ratings: projectFilters.smm_ratings,
      funded_by: projectFilters.funded_by,
      project_revenue_converted: projectFilters.project_revenue_converted,
    }),
  };
}

type ProjectDetailsRow = {
  account?: string | null;
  project_name?: string | null;
  project_code?: string | null;
  status?: string | null;
  rag_status?: string | null;
  timeline_breach?: string | null;
  estimated_end_date?: string | null;
  start_date?: string | null;
  project_revenue_converted?: number | null;
  recognized_revenue?: number | null;
  realized_revenue?: number | null;
  efforts_deviation?: string | null;
  unrecognized_revenue?: number | null;
  unassigned_role_count?: number | null;
};

function isRedRag(project: { rag_status?: string | null }): boolean {
  return project.rag_status === "Red";
}

function isOverEffortsBudget(project: { efforts_deviation?: string | null }): boolean {
  const v = project.efforts_deviation?.trim();
  return v === "Over";
}

function isTimelineBreach(project: { timeline_breach?: string | null }): boolean {
  return project.timeline_breach === "Yes";
}

function isInProgress(project: { status?: string | null }): boolean {
  return project.status?.trim() === "In Progress";
}

function isInProgressOrOnHold(project: { status?: string | null }): boolean {
  const s = project.status?.trim();
  return s === "In Progress" || s === "On Hold";
}

/** Whole days from project start (local midnight) to today (local midnight). */
function calendarDaysSinceProjectStart(startDate: string | null | undefined): number | null {
  if (!startDate) return null;
  const start = new Date(startDate.split("T")[0]);
  if (Number.isNaN(start.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  start.setHours(0, 0, 0, 0);
  return Math.round((today.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
}

function isNoRevenueIp30Plus(project: ProjectDetailsRow): boolean {
  if (!isInProgress(project)) return false;
  const rec = project.recognized_revenue;
  const recognized = typeof rec === "number" ? rec : 0;
  if (recognized > 0) return false;
  const days = calendarDaysSinceProjectStart(project.start_date);
  if (days === null) return false;
  return days >= 30;
}

/** Calendar days from estimated end (00:00) to today (00:00). Negative if end is still in the future. */
function calendarDaysFromEstimatedEndToToday(estimatedEnd: string | null | undefined): number | null {
  if (!estimatedEnd) return null;
  const endParts = estimatedEnd.split("T")[0];
  const end = new Date(endParts);
  if (Number.isNaN(end.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return Math.round((today.getTime() - end.getTime()) / (1000 * 60 * 60 * 24));
}

interface WatchRow {
  key: string;
  title: string;
  subtitle: string;
  valueLabel: string;
}

type WatchTabId =
  | "red-rag-accounts"
  | "red-rag-projects"
  | "timeline-breach"
  | "pending-recognition"
  | "pending-realization"
  | "over-budget-accounts"
  | "no-revenue-ip"
  | "unallocated-ip";

const WATCH_TABS: { id: WatchTabId; label: string }[] = [
  { id: "red-rag-projects", label: "Red RAG · projects" },
  { id: "red-rag-accounts", label: "Red RAG · accounts" },
  { id: "over-budget-accounts", label: "Over budget · accounts" },
  { id: "timeline-breach", label: "Timeline breach" },
  { id: "pending-recognition", label: "Pending recognition" },
  { id: "pending-realization", label: "Pending realization" },
  { id: "no-revenue-ip", label: "No revenue · IP 30d+" },
  { id: "unallocated-ip", label: "Unallocated · projects (IP)" },
];

const WATCH_TAB_TO_INSIGHT_CARD: Record<WatchTabId, ActionableInsightCardId> = {
  "red-rag-accounts": "rag-red-accounts",
  "red-rag-projects": "rag-red-projects",
  "timeline-breach": "timeline-breach",
  "pending-recognition": "pending-recognition",
  "pending-realization": "pending-realization",
  "over-budget-accounts": "over-budget",
  "no-revenue-ip": "no-revenue-ip",
  "unallocated-ip": "unallocated-capacity",
};

function WatchListRows({ rows, emptyHint }: { rows: WatchRow[]; emptyHint: string }) {
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
        {emptyHint}
      </Typography>
    );
  }
  return (
    <>
      {rows.map((row, idx) => (
        <Box
          key={row.key}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 2,
            py: 1.25,
            borderTop: idx === 0 ? "none" : "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(SEARCE_COLORS.error, 0.12),
              color: SEARCE_COLORS.error,
              fontWeight: 800,
              fontSize: 14,
            }}
          >
            {idx + 1}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700} noWrap title={row.title}>
              {row.title}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
              title={row.subtitle}
            >
              {row.subtitle}
            </Typography>
          </Box>
          <Typography variant="body2" fontWeight={700} sx={{ flexShrink: 0, textAlign: "right" }}>
            {row.valueLabel}
          </Typography>
        </Box>
      ))}
    </>
  );
}

export interface ProjectActionableInsightsProps {
  projectFilters?: ProjectsFilterRequest;
  /** Same gate as Project details table — avoids calling project-details before filter options exist. */
  filterOptionsReady: boolean;
  /** Apply table filters for this insight, focus Project Details tab, and scroll to the tables section. */
  onNavigateWithInsight?: (cardId: ActionableInsightCardId) => void;
}

const ProjectActionableInsights: React.FC<ProjectActionableInsightsProps> = ({
  projectFilters,
  filterOptionsReady,
  onNavigateWithInsight,
}) => {
  const [watchTab, setWatchTab] = useState<WatchTabId>("red-rag-projects");

  const minimalApiFilters = useMemo(
    () => buildMinimalProjectDetailsFilters(projectFilters),
    [projectFilters]
  );

  const { data, isLoading } = useQuery({
    queryKey: ["project-details-all", minimalApiFilters],
    queryFn: async () =>
      dashboardApi.getProjectDetails({
        ...minimalApiFilters,
        page: 1,
        page_size: 10000,
      }),
    staleTime: 2 * 60 * 1000,
    enabled: filterOptionsReady,
  });

  const projects = (data?.projects ?? []) as ProjectDetailsRow[];

  const metrics = useMemo(() => {
    const redProjects = projects.filter((p) => isRedRag(p) && isInProgressOrOnHold(p));
    const redAccounts = new Set(
      redProjects.map((p: { account?: string | null }) => p.account).filter(Boolean)
    );
    const overBudget = projects.filter((p) => isOverEffortsBudget(p) && isInProgressOrOnHold(p));
    const timelineBreaches = projects.filter((p) => isTimelineBreach(p) && isInProgressOrOnHold(p));
    const pendingRecognition = projects.reduce((sum, p) => {
      const v = p.unrecognized_revenue;
      return sum + (typeof v === "number" ? v : 0);
    }, 0);

    const pendingRealization = projects.reduce((sum, p) => {
      const rec = typeof p.recognized_revenue === "number" ? p.recognized_revenue : 0;
      const real = typeof p.realized_revenue === "number" ? p.realized_revenue : 0;
      const delta = rec - real;
      return sum + (delta > 0 ? delta : 0);
    }, 0);

    const noRevenueGapProjects = projects.filter(isNoRevenueIp30Plus);
    /** Project row count — matches Project Details when the same no-revenue filters are applied. */
    const noRevenueProjectCount = noRevenueGapProjects.length;

    const unallocatedRolesTotal = projects.reduce((sum, p) => {
      const n = p.unassigned_role_count;
      return sum + (typeof n === "number" && n > 0 ? n : 0);
    }, 0);

    return {
      redAccountCount: redAccounts.size,
      redProjectCount: redProjects.length,
      overBudgetCount: overBudget.length,
      timelineBreachCount: timelineBreaches.length,
      pendingRecognitionTotal: pendingRecognition,
      pendingRealizationTotal: pendingRealization,
      noRevenueProjectCount,
      unallocatedRolesTotal,
    };
  }, [projects]);

  const watchLists = useMemo(() => {
    const redProjects = projects.filter((p) => isRedRag(p) && isInProgressOrOnHold(p));

    const revenueByAccount = new Map<string, number>();
    const redProjectCountByAccount = new Map<string, number>();
    for (const p of redProjects) {
      const acc = p.account?.trim();
      if (!acc) continue;
      const rev = typeof p.project_revenue_converted === "number" ? p.project_revenue_converted : 0;
      revenueByAccount.set(acc, (revenueByAccount.get(acc) ?? 0) + rev);
      redProjectCountByAccount.set(acc, (redProjectCountByAccount.get(acc) ?? 0) + 1);
    }

    const redRagAccountsTop: WatchRow[] = Array.from(revenueByAccount.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([account, revenue]) => ({
        key: `acc-${account}`,
        title: account,
        subtitle: `${redProjectCountByAccount.get(account) ?? 0} Red RAG project${
          (redProjectCountByAccount.get(account) ?? 0) === 1 ? "" : "s"
        } · ranked by account revenue`,
        valueLabel: formatCurrency(revenue),
      }));

    const redRagProjectsTop: WatchRow[] = [...redProjects]
      .sort(
        (a, b) =>
          (typeof b.project_revenue_converted === "number" ? b.project_revenue_converted : 0) -
          (typeof a.project_revenue_converted === "number" ? a.project_revenue_converted : 0)
      )
      .slice(0, 3)
      .map((p) => ({
        key: `proj-${p.project_code ?? p.project_name ?? ""}`,
        title: p.project_name?.trim() || p.project_code || "—",
        subtitle: p.account ? String(p.account) : "Ranked by project revenue",
        valueLabel: formatCurrency(
          typeof p.project_revenue_converted === "number" ? p.project_revenue_converted : 0
        ),
      }));

    const timelineBreachTop: WatchRow[] = projects
      .filter((p) => isTimelineBreach(p) && isInProgressOrOnHold(p))
      .map((p) => {
        const raw = calendarDaysFromEstimatedEndToToday(p.estimated_end_date);
        const overdueDays =
          raw === null || !Number.isFinite(raw) ? null : Math.max(0, raw);
        return { p, overdueDays };
      })
      .sort((a, b) => {
        const av = a.overdueDays ?? -1;
        const bv = b.overdueDays ?? -1;
        if (bv !== av) return bv - av;
        const ar = typeof a.p.project_revenue_converted === "number" ? a.p.project_revenue_converted : 0;
        const br = typeof b.p.project_revenue_converted === "number" ? b.p.project_revenue_converted : 0;
        return br - ar;
      })
      .slice(0, 3)
      .map(({ p, overdueDays }) => {
        const endHint = p.estimated_end_date
          ? `Est. end ${p.estimated_end_date.split("T")[0]}`
          : "No estimated end date";
        const acct = p.account?.trim();
        return {
          key: `breach-${p.project_code ?? p.project_name ?? ""}`,
          title: p.project_name?.trim() || p.project_code || "—",
          subtitle: [
            acct,
            endHint,
            overdueDays !== null ? `${overdueDays} day${overdueDays === 1 ? "" : "s"} past plan` : "Slip vs plan (date n/a)",
          ]
            .filter(Boolean)
            .join(" · "),
          valueLabel: overdueDays !== null ? `${overdueDays}d` : "—",
        };
      });

    const pendingRecognitionTop: WatchRow[] = [...projects]
      .filter((p) => typeof p.unrecognized_revenue === "number" && p.unrecognized_revenue > 0)
      .sort(
        (a, b) =>
          (typeof b.unrecognized_revenue === "number" ? b.unrecognized_revenue : 0) -
          (typeof a.unrecognized_revenue === "number" ? a.unrecognized_revenue : 0)
      )
      .slice(0, 3)
      .map((p) => {
        const amt =
          typeof p.unrecognized_revenue === "number" ? p.unrecognized_revenue : 0;
        return {
          key: `pending-${p.project_code ?? p.project_name ?? ""}`,
          title: p.project_name?.trim() || p.project_code || "—",
          subtitle: p.account
            ? `${p.account} · ranked by unrecognized revenue`
            : "Ranked by unrecognized revenue",
          valueLabel: formatCurrency(amt),
        };
      });

    const pendingRealizationTop: WatchRow[] = [...projects]
      .map((p) => {
        const rec = typeof p.recognized_revenue === "number" ? p.recognized_revenue : 0;
        const real = typeof p.realized_revenue === "number" ? p.realized_revenue : 0;
        return { p, delta: rec - real };
      })
      .filter(({ delta }) => delta > 0)
      .sort((a, b) => b.delta - a.delta)
      .slice(0, 3)
      .map(({ p, delta }) => ({
        key: `realization-${p.project_code ?? p.project_name ?? ""}`,
        title: p.project_name?.trim() || p.project_code || "—",
        subtitle: p.account
          ? `${p.account} · ranked by unrealized revenue`
          : "Ranked by unrealized revenue",
        valueLabel: formatCurrency(delta),
      }));

    const overBudgetProjects = projects.filter((p) => isOverEffortsBudget(p) && isInProgressOrOnHold(p));
    const overRevByAccount = new Map<string, number>();
    const overProjectCountByAccount = new Map<string, number>();
    for (const p of overBudgetProjects) {
      const acc = p.account?.trim();
      if (!acc) continue;
      const rev = typeof p.project_revenue_converted === "number" ? p.project_revenue_converted : 0;
      overRevByAccount.set(acc, (overRevByAccount.get(acc) ?? 0) + rev);
      overProjectCountByAccount.set(acc, (overProjectCountByAccount.get(acc) ?? 0) + 1);
    }

    const overBudgetAccountsTop: WatchRow[] = Array.from(overRevByAccount.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([account, revenue]) => ({
        key: `over-acc-${account}`,
        title: account,
        subtitle: `${overProjectCountByAccount.get(account) ?? 0} over-budget project${
          (overProjectCountByAccount.get(account) ?? 0) === 1 ? "" : "s"
        } (efforts Over) · ranked by account revenue`,
        valueLabel: formatCurrency(revenue),
      }));

    const noRevenueIpTop: WatchRow[] = projects
      .filter(isNoRevenueIp30Plus)
      .map((p) => {
        const days = calendarDaysSinceProjectStart(p.start_date);
        return { p, days: days ?? -Infinity };
      })
      .sort((a, b) => b.days - a.days)
      .slice(0, 3)
      .map(({ p, days }) => {
        const d = Number.isFinite(days) ? Math.round(days) : null;
        return {
          key: `norev-${p.project_code ?? p.project_name ?? ""}`,
          title: p.project_name?.trim() || p.project_code || "—",
          subtitle: [
            p.account?.trim(),
            "$0 recognized",
            d !== null ? `${d} days in progress` : "In progress (start date n/a)",
          ]
            .filter(Boolean)
            .join(" · "),
          valueLabel: d !== null ? `${d}d` : "—",
        };
      });

    const unallocatedIpTop: WatchRow[] = projects
      .filter((p) => (typeof p.unassigned_role_count === "number" ? p.unassigned_role_count : 0) > 0)
      .sort(
        (a, b) =>
          (typeof b.unassigned_role_count === "number" ? b.unassigned_role_count : 0) -
          (typeof a.unassigned_role_count === "number" ? a.unassigned_role_count : 0)
      )
      .slice(0, 3)
      .map((p) => {
        const n = typeof p.unassigned_role_count === "number" ? p.unassigned_role_count : 0;
        return {
          key: `unalloc-${p.project_code ?? p.project_name ?? ""}`,
          title: p.project_name?.trim() || p.project_code || "—",
          subtitle: p.account?.trim()
            ? `${p.account} · unassigned roles (all statuses)`
            : "Unassigned roles (all statuses)",
          valueLabel: `${n} role${n === 1 ? "" : "s"}`,
        };
      });

    return {
      redRagAccountsTop,
      pendingRecognitionTop,
      pendingRealizationTop,
      noRevenueIpTop,
      redRagProjectsTop,
      timelineBreachTop,
      overBudgetAccountsTop,
      unallocatedIpTop,
    };
  }, [projects]);

  const cards: InsightCardDef[] = useMemo(
    () => [
      {
        id: "rag-red-projects",
        badge: "PRIORITY",
        tone: "priority",
        title: "RAG status — Red projects",
        metric: String(metrics.redProjectCount),
        subtext: "Red RAG projects that are In Progress or On Hold in this portfolio view.",
        actionLabel: "View →",
        icon: <DescriptionRounded />,
      },
      {
        id: "pending-recognition",
        badge: "REVENUE",
        tone: "revenue",
        title: "Pending recognition",
        metric: formatCurrency(metrics.pendingRecognitionTotal),
        subtext:
          "Delivered work not yet recognized (all project statuses; sum of unrecognized revenue).",
        actionLabel: "View →",
        icon: <AttachMoneyRounded />,
      },
      {
        id: "pending-realization",
        badge: "REVENUE",
        tone: "revenue",
        title: "Pending realization",
        metric: formatCurrency(metrics.pendingRealizationTotal),
        subtext:
          "Recognized revenue not yet realized (all project statuses; sum of recognized minus realized revenue).",
        actionLabel: "View →",
        icon: <SavingsRounded />,
      },
      {
        id: "no-revenue-ip",
        badge: "GAP",
        tone: "gap",
        title: "No revenue (IP ≥ 30d)",
        metric: String(metrics.noRevenueProjectCount),
        subtext: "In Progress 30+ days with $0 recognized revenue (same count as filtered project rows below).",
        actionLabel: "View projects →",
        icon: <MoneyOffRounded />,
      },
      // {
      //   id: "rag-red-accounts",
      //   badge: "PRIORITY",
      //   tone: "priority",
      //   title: "RAG status — Red accounts",
      //   metric: String(metrics.redAccountCount),
      //   subtext:
      //     "Accounts with Red RAG projects (In Progress or On Hold only). Opens Project Details with the same scope.",
      //   actionLabel: "View →",
      //   icon: <ReportProblemRounded />,
      // },
      {
        id: "over-budget",
        badge: "COST",
        tone: "cost",
        title: "Over budget (efforts)",
        metric: String(metrics.overBudgetCount),
        subtext: "Spend above plan (efforts Over) for In Progress or On Hold projects only.",
        actionLabel: "View →",
        icon: <WarningRounded />,
      },
      {
        id: "timeline-breach",
        badge: "SCHEDULE",
        tone: "schedule",
        title: "Timeline breach",
        metric: String(metrics.timelineBreachCount),
        subtext:
          "Timeline breach flagged Yes, for In Progress or On Hold projects only.",
        actionLabel: "View →",
        icon: <AccessTimeRounded />,
      },
      {
        id: "unallocated-capacity",
        badge: "CAPACITY",
        tone: "capacity",
        title: "Unallocated",
        metric: String(metrics.unallocatedRolesTotal),
        subtext:
          "Unassigned roles summed across all project statuses (staffing gaps).",
        actionLabel: "View projects →",
        icon: <GroupsRounded />,
      },
    ],
    [metrics]
  );

  const handleInsightLinkClick = (e: React.MouseEvent, cardId: ActionableInsightCardId) => {
    e.preventDefault();
    onNavigateWithInsight?.(cardId);
  };

  const watchTabInsightId = WATCH_TAB_TO_INSIGHT_CARD[watchTab];

  const watchTabFooterCaption: Record<WatchTabId, string> = {
    "red-rag-accounts":
      "Top 3 accounts by summed Red RAG project revenue (In Progress or On Hold only).",
    "red-rag-projects":
      "Top 3 Red RAG projects by project revenue (In Progress or On Hold only).",
    "timeline-breach":
      "Top 3 timeline breaches (In Progress or On Hold) by days past estimated end; missing dates last.",
    "pending-recognition": "Top 3 projects by unrecognized revenue (all project statuses).",
    "pending-realization": "Top 3 projects by unrealized revenue (recognized minus realized, all statuses).",
    "over-budget-accounts":
      "Top 3 accounts by revenue among Over-budget efforts projects (In Progress or On Hold only).",
    "no-revenue-ip":
      "Top 3 In Progress projects with $0 recognized for 30+ days, ranked by longest time in progress.",
    "unallocated-ip":
      "Top 3 projects by unassigned role count (all statuses; open roles not yet staffed).",
  };

  if (!filterOptionsReady) {
    return null;
  }

  return (
    <Box sx={{ mb: 0 }}>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
        Actionable insights
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 900 }}>
        Portfolio signals from RAG, efforts vs. plan, timeline, and revenue — scoped to your filters.
        Use the links on each card to apply matching filters in Project Details and jump to the table.
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <Typography variant="caption" fontWeight={700} letterSpacing={0.08} color="text.secondary">
          INSIGHT CATALOG
        </Typography>
       
      </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", gap: 2, overflow: "hidden" }}>
          {[1, 2, 3, 4, 5, 6, 7].map((k) => (
            <Skeleton
              key={k}
              variant="rounded"
              sx={{ minWidth: 220, height: 168, flexShrink: 0, borderRadius: 2 }}
              animation="wave"
            />
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            gap: 2,
            overflowX: "auto",
            pb: 1,
            scrollSnapType: "x mandatory",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {cards.map((card) => {
            const c = toneColors[card.tone];
            return (
              <Paper
                key={card.id}
                elevation={0}
                sx={{
                  minWidth: { xs: 240, sm: 220 },
                  maxWidth: 280,
                  flex: "0 0 auto",
                  scrollSnapAlign: "start",
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderTop: `4px solid ${c.border}`,
                  bgcolor: "background.paper",
                  boxShadow: (theme) => theme.shadows[1],
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0, flex: 1 }}>
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: "50%",
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: c.iconBg,
                        color: c.iconFg,
                        "& .MuiSvgIcon-root": { fontSize: 17 },
                      }}
                    >
                      {card.icon}
                    </Box>
                    <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.05, letterSpacing: -0.02 }}>
                      {card.metric}
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      bgcolor: c.badgeBg,
                      color: c.iconFg,
                      flexShrink: 0,
                    }}
                  >
                    {card.badge}
                  </Typography>
                </Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                  {card.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1, fontSize: 13 }}>
                  {card.subtext}
                </Typography>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  disabled={!onNavigateWithInsight}
                  onClick={(e) => handleInsightLinkClick(e, card.id)}
                  sx={{ alignSelf: "flex-start", fontWeight: 600, cursor: "pointer" }}
                >
                  {card.actionLabel}
                </Link>
              </Paper>
            );
          })}
        </Box>
      )}

      {isLoading ? (
        <Skeleton
          variant="rounded"
          animation="wave"
          sx={{ mt: 3, height: { xs: 280, md: 322 }, borderRadius: 2 }}
        />
      ) : (
        <Box sx={{ mt: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
              mb: 1.5,
            }}
          >
            <Typography variant="subtitle1" fontWeight={700}>
              Projects and accounts to watch
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ maxWidth: 440, textAlign: { xs: "left", sm: "right" } }}
            >
              Ranked lists · tabs switch the lens · opens matching filters in Project Details
            </Typography>
          </Box>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              borderLeft: `4px solid ${SEARCE_COLORS.error}`,
              bgcolor: "background.paper",
              boxShadow: (theme) => theme.shadows[1],
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                px: 2,
                py: 1.5,
                borderBottom: "1px solid",
                borderColor: "divider",
                bgcolor: (theme) => alpha(theme.palette.text.primary, 0.02),
              }}
            >
              {WATCH_TABS.map((t) => {
                const selected = watchTab === t.id;
                return (
                  <Button
                    key={t.id}
                    type="button"
                    variant="text"
                    disableElevation
                    onClick={() => setWatchTab(t.id)}
                    sx={{
                      borderRadius: 999,
                      px: 2,
                      py: 0.65,
                      minWidth: 0,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: 13,
                      lineHeight: 1.25,
                      border: "1px solid",
                      borderColor: selected ? "primary.main" : "divider",
                      bgcolor: selected ? "primary.main" : "background.paper",
                      color: selected ? "primary.contrastText" : "text.primary",
                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: selected ? "primary.dark" : (theme) => alpha(theme.palette.primary.main, 0.06),
                        borderColor: selected ? "primary.dark" : "text.disabled",
                      },
                    }}
                  >
                    {t.label}
                  </Button>
                );
              })}
            </Box>

            <Box sx={{ pt: 1 }}>
              {watchTab === "red-rag-projects" && (
                <WatchListRows
                  rows={watchLists.redRagProjectsTop}
                  emptyHint="No Red RAG projects in this portfolio view."
                />
              )}
              {watchTab === "red-rag-accounts" && (
                <WatchListRows
                  rows={watchLists.redRagAccountsTop}
                  emptyHint="No Red RAG accounts in this portfolio view."
                />
              )}
              {watchTab === "over-budget-accounts" && (
                <WatchListRows
                  rows={watchLists.overBudgetAccountsTop}
                  emptyHint="No over-budget accounts in this portfolio view."
                />
              )}
              {watchTab === "timeline-breach" && (
                <WatchListRows
                  rows={watchLists.timelineBreachTop}
                  emptyHint="No timeline-breach projects in this portfolio view."
                />
              )}
              {watchTab === "pending-recognition" && (
                <WatchListRows
                  rows={watchLists.pendingRecognitionTop}
                  emptyHint="No projects with pending recognition in this portfolio view."
                />
              )}
              {watchTab === "pending-realization" && (
                <WatchListRows
                  rows={watchLists.pendingRealizationTop}
                  emptyHint="No projects with pending realization in this portfolio view."
                />
              )}
              {watchTab === "no-revenue-ip" && (
                <WatchListRows
                  rows={watchLists.noRevenueIpTop}
                  emptyHint="No In Progress projects with $0 recognized for 30+ days in this view."
                />
              )}
              {watchTab === "unallocated-ip" && (
                <WatchListRows
                  rows={watchLists.unallocatedIpTop}
                  emptyHint="No In Progress projects with unassigned roles in this portfolio view."
                />
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                px: 2,
                py: 1.5,
                borderTop: "1px solid",
                borderColor: "divider",
                bgcolor: (theme) => alpha(theme.palette.text.primary, 0.02),
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 560 }}>
                {watchTabFooterCaption[watchTab]}
              </Typography>
              <Link
                component="button"
                type="button"
                variant="caption"
                disabled={!onNavigateWithInsight}
                onClick={(e) => handleInsightLinkClick(e, watchTabInsightId)}
                sx={{ fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
              >
                Go to Project Details
              </Link>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default ProjectActionableInsights;
